import scoringManager from '../managers/ScoringManager';
import responseManager from '../managers/ResponseManager';
import lessonManager from '../managers/LessonManager';

import studentActivityManager from '../managers/StudentActivityManager';

import UtilsService from './UtilsService';
import Auth from './AuthService';
import { isRubricCheckboxScoring } from '../utils/rubricCheckboxScoring';
import { parseScoreNumber } from '../../Constants';

export class ScoringService {
  fetchContentItemRubric = async (lessonElementId, contentItem, forResource) => {
    const existingRubric = scoringManager.rubricMap.get(lessonElementId);
    if (existingRubric && typeof existingRubric.rubricCheckboxScoring !== 'undefined') {
      return true;
    }
    let url = '';
    if (forResource) {
      url = `${Auth.ecms}/api/viewRubric?rubricId=${ contentItem.rubricEntityId }`;
    } else {
      url = `${Auth.ecms}/api/viewRubricForQuestion?contentItemId=${contentItem.id}`;
    }
    try {
      const response = await Auth.fetch(url, {
        method: 'GET'
      });

      if (response.status === 'SUCCESS') {
        const rubricPayload = response?.rubric || response?.data || response;
        const rubric = {
          ...rubricPayload,
          rubricCheckboxScoring: isRubricCheckboxScoring({
            ...rubricPayload,
            rubricCheckboxScoring: response?.rubricCheckboxScoring ?? rubricPayload?.rubricCheckboxScoring,
            checkboxScoring: response?.checkboxScoring ?? rubricPayload?.checkboxScoring,
            rubric_checkbox_scoring: response?.rubric_checkbox_scoring ?? rubricPayload?.rubric_checkbox_scoring
          })
        };
        const state = responseManager.getLessonElementState(lessonElementId);
        if (rubric && rubric.categories && rubric.categories.length > 0) {
          scoringManager.setRubric(lessonElementId, rubric, state.isScored);
        }
        return true;
      } else {
        console.warn(response.status);
        console.warn(response.statusMessage);
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  setCategoryTempScoreFromTable = (lessonElementId, categoryId, str) => {
    const rubric = scoringManager.rubricMap.get(lessonElementId);
    const category = rubric?.categories?.find((c) => c.categoryId === categoryId);
    const previous = category?.calculatedTempScore;
    scoringManager.setCategoryTempScoreFromTable(lessonElementId, categoryId, str);
    if (!this.areScoreValuesEqual(previous, str)) {
      scoringManager.markScoreDirty(lessonElementId);
    }
  };

  setCategoryFullCreditFromTable = (lessonElementId) => {
    scoringManager.setCategoryFullCreditFromTable(lessonElementId);
    scoringManager.markScoreDirty(lessonElementId);
  };

  setCategoryCheckboxScore = (lessonElementId, categoryId, checked) => {
    scoringManager.setCategoryCheckboxScore(lessonElementId, categoryId, checked);
    scoringManager.markScoreDirty(lessonElementId);
  };

  /**
   * Empty rubric category blur should not invent a zero for an unscored item.
   * Already-scored items still coerce a blank field to 0.00.
   */
  setCategoryTempScoreFromTableOnBlur = (lessonElementId, categoryId) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    const rubric = scoringManager.rubricMap.get(lessonElementId);
    const category = rubric?.categories?.find((c) => c.categoryId === categoryId);
    if (!state?.isScored && UtilsService.isNullOrEmpty(category?.calculatedTempScore)) {
      return;
    }
    this.setCategoryTempScoreFromTable(lessonElementId, categoryId, state?.isScored ? '0.00' : '');
  };

  rubricCellClick = (lessonElementId, clickIndex, categoryId) => {
    scoringManager.rubricCellClick(lessonElementId,
      clickIndex, categoryId);
    scoringManager.markScoreDirty(lessonElementId);
  }

  initTempScores = () => {
    scoringManager.tempScores.clear();
    scoringManager.clearAllScoreDirty();

    const testItems = lessonManager.elementsThatHaveState;
    testItems.forEach((model) => {
      if (model.isTestItem && !model.unscored) {
        const state = responseManager.getLessonElementState(model.lessonElementId);

        if (state && state.isScored) {
          scoringManager.setTempScore(model.lessonElementId, parseScoreNumber(state.submittedItemScore, 0));
        } else if (state && !state.isScored) {
          scoringManager.setTempScore(model.lessonElementId, '');
        }
      }
    });
  }

  setTempScore = (lessonElementId, score) => {
    const previous = scoringManager.tempScores.has(lessonElementId) ?
      scoringManager.tempScores.get(lessonElementId) :
      '';
    scoringManager.setTempScore(lessonElementId, score);
    if (!this.areScoreValuesEqual(previous, score)) {
      scoringManager.markScoreDirty(lessonElementId);
    }
  }

  areScoreValuesEqual = (left, right) => {
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty && rightEmpty) {
      return true;
    }
    return String(left) === String(right);
  }

  isEmptyScoreValue = (score) => {
    return score === null || score === undefined || score === '' ||
      (typeof score === 'string' && score.length === 0);
  }

  updateTestItemScore = async (lessonElementId, { force = false } = {}) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    const state = responseManager.getLessonElementState(lessonElementId);
    if (!model?.isTestItem || !state) {
      return;
    }

    const { activityId } = studentActivityManager;
    const { studentId } = studentActivityManager;
    const scoreDirty = scoringManager.isScoreDirty(lessonElementId);

    // Implicit saves (exit, next item) must not mark an untouched item as 0.
    // Opening a rubric in score mode initializes empty categories to 0.00.
    if (!force && !scoreDirty) {
      return;
    }

    scoringManager.normalizeCheckboxRubricScores(lessonElementId);

    let score = scoringManager.getTempScore(lessonElementId);
    const rubric = scoringManager.rubricMap.get(lessonElementId);
    const checkboxMode = rubric && isRubricCheckboxScoring(rubric);
    if (score !== null && score?.length === 0 && !checkboxMode) {
      score = null;
    }
    if (!state.isScored && this.isEmptyScoreValue(score)) {
      scoringManager.clearScoreDirty(lessonElementId);
      return;
    }
    const parsedScore = parseScoreNumber(score, Number.NaN);
    const parsedSubmitted = parseScoreNumber(state.submittedItemScore, Number.NaN);
    if (score === state.submittedItemScore ||
      (state.isScored && Number.isFinite(parsedScore) && parsedScore === parsedSubmitted && !scoreDirty)) {
      scoringManager.clearScoreDirty(lessonElementId);
      return;
    }

    const subscore = scoringManager.getSubscore(lessonElementId);
    // Make sure we have an activityId
    if (!UtilsService.isNullOrEmpty(activityId)) {
      try {
        const postBody = {
          activityId,
          studentId,
          lessonElementId,
          subscore,
          score
        };

        const result = await Auth.fetch(`${Auth.ecms}/api/updateTestItemScore`, {
          method: 'POST',
          body: postBody,
        });
        if (result.status === 'SUCCESS') {
          responseManager.setScore(lessonElementId, model, score, subscore, result.scoreState);
          scoringManager.clearScoreDirty(lessonElementId);

          return true;
        }
      } catch (e) {
        console.error(e);
        console.error('No activity id found.   updateTestItemScore function skipped.');
      }
    } else {
      lessonManager.setLoaded(0);
      console.warn('No activity id found.   updateTestItemScore function skipped.');
    }
  }

  clearRubricSelections = (lessonElementId) => {
    scoringManager.clearRubricSelections(lessonElementId);
  };

  submitTeacherComment = async (lessonElementId) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    // Make sure we have an activityId
    if (state) {
      try {
        const postBody = {
          entityId: state.responseId,
          entityTypeId: 'response',
          comment: state.comment
        };
        const result = await Auth.fetch(`${Auth.ecms}/api/addUpdateComment`, {
          method: 'POST',
          body: postBody,
        });
        if (result.status === 'SUCCESS') {
          state.setComment(result.comment);
          return true;
        }
      } catch (e) {
        console.error(e);
        console.warn('No activity id found.   submitAssignmentComment function skipped.');
      }
    } else {
      console.warn('No activity id found.   submitAssignmentComment function skipped.');
    }
  };

  /**
   * Gets AI score data including total score and category scores
   * @param {string} lessonElementId - The lesson element ID
   * @returns {Promise<{totalScore: number, categoryScores: Object}|null>} AI scores or null
   */
  getAiScores = async (lessonElementId) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    if (!state || !state.bedrockUsageId) {
      return null;
    }
    try {
      const aiScoreData = await this.fetchAiScoreData(state.bedrockUsageId);
      if (aiScoreData && aiScoreData.length > 0) {
        const aiData = aiScoreData[0];

        // Calculate AI total score
        let totalScore = null;
        if (aiData?.rubricCriteria?.length > 0) {
          // For rubric items, calculate from rubric criteria
          totalScore = aiData.rubricCriteria.reduce((total, criteria) => {
            return total + (parseFloat(criteria.score) || 0);
          }, 0);
        } else if (aiData?.overallScore?.score) {
          // For non-rubric items, use overallScore
          totalScore = parseFloat(aiData.overallScore.score);
        }

        // Calculate category scores if rubric criteria exist
        const categoryScores = {};
        if (aiData?.rubricCriteria?.length > 0) {
          aiData.rubricCriteria.forEach((aiCriteria) => {
            const criteriaName = aiCriteria.criteria;
            const score = parseFloat(aiCriteria.score) || 0;
            categoryScores[criteriaName] = (categoryScores[criteriaName] || 0) + score;
          });
        }

        return { totalScore, categoryScores };
      }

      return null;
    } catch (e) {
      console.error('Error getting AI scores:', e);
      return null;
    }
  };

  /**
   * Check if the current scores have changed from AI-suggested scores.
   * Only compares category scores (not total score, as total is calculated from categories).
   * Returns null if check is not applicable (wrong scoreState or no AI data).
   * Returns false if scores match AI suggestions.
   * Returns true if scores have been manually changed from AI suggestions.
   *
   * @param {string} lessonElementId - The lesson element ID
   * @param {string} scoreState - The current score state
   * @returns {Promise<boolean|null>} - null if not applicable, false if matches AI, true if changed from AI
   */
  checkIfChangedFromAiScore = async (lessonElementId, scoreState) => {
    const { ScoreState } = require('../../Constants');

    // Don't check if scoreState is not AI-related
    if (scoreState !== ScoreState.AI_SCORED && scoreState !== ScoreState.AI_OVERRIDDEN) {
      return null;
    }

    const aiScores = await this.getAiScores(lessonElementId);

    if (!aiScores) {
      return null;
    }

    // Check if rubric scored - if so, compare individual category scores
    const rubric = scoringManager.rubricMap.get(lessonElementId);
    if (rubric && Object.keys(aiScores.categoryScores).length > 0) {
      for (const category of rubric.categories) {
        const currentScore = parseFloat(category.calculatedTempScore || 0);
        const aiScore = aiScores.categoryScores[category.name] || 0;

        if (Math.abs(currentScore - aiScore) > 0.01) {
          return true; // Scores do not match, so they have been changed
        }
      }
      return false; // All category scores match AI
    }

    // If no rubric, compare total scores
    if (aiScores.totalScore !== null) {
      const currentTotalScore = parseFloat(scoringManager.getTempScore(lessonElementId) || 0);
      if (Math.abs(currentTotalScore - aiScores.totalScore) > 0.01) {
        return true; // Total score does not match
      }
      return false; // Total score matches
    }

    return null;
  };

  /**
   * Sets the current score back to the original AI-suggested score
   * @param {string} lessonElementId - The lesson element ID
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  setToAiSuggestedScore = async (lessonElementId) => {
    const aiScores = await this.getAiScores(lessonElementId);

    if (!aiScores || aiScores.totalScore === null) {
      return false;
    }

    try {
      // Restore rubric category scores if they exist
      const rubric = scoringManager.rubricMap.get(lessonElementId);
      if (rubric && Object.keys(aiScores.categoryScores).length > 0) {
        rubric.categories.forEach((category) => {
          const aiScore = aiScores.categoryScores[category.name];
          if (aiScore !== undefined) {
            this.setCategoryTempScoreFromTable(lessonElementId, category.categoryId, aiScore.toString());
          }
        });
      }

      // Update the temp score to the AI total score
      this.setTempScore(lessonElementId, aiScores.totalScore.toString());

      return true;
    } catch (e) {
      console.error('Error setting to AI suggested score:', e);
      return false;
    }
  };

  /**
   * Fetches AI score data for a given bedrock usage ID
   * @param {string} bedrockUsageId - The bedrock usage ID from the AI grading response
   * @returns {Promise<Array|null>} The AI score data array, or null if not found
   */
  fetchAiScoreData = async (bedrockUsageId) => {
    const result = await Auth.fetch(`${Auth.ecms}/api/viewAiScoreCard?bedrockUsageId=${bedrockUsageId}`, {
      method: 'GET',
    });
    if (result.status === 'SUCCESS') {
      return result.data;
    }
    return null;
  };
}
export default new ScoringService();
